/**
 * React hook for commercial lines rating — start runs, SSE stream, resume.
 */

import { useEffect, useRef, useState } from 'react';
const CL_BASE = `${process.env.REACT_APP_MyAMP_Url}/api/ams/commercial-rating`;
const SKILLS_BASE = `${process.env.REACT_APP_MyAMP_Url}/api/ams/underwriting-skills`;

// ── Types ──

export interface CLRunStep {
  stepId: string;
  stepIndex: number;
  stepLabel: string;
  status: string;
  durationMs: number | null;
  error: string | null;
  progress: { key: string; label: string; status: string; detail?: string; ts?: string }[];
  awaitingInput: { prompt: string; fields: any[] } | null;
}

export interface CLRunState {
  runId: string;
  status: string;
  durationMs: number | null;
  error: string | null;
  result: any | null;
  steps: CLRunStep[];
}

// ── Start a run ──

export async function startCommercialRun(payload: {
  row_id: string;
  insured_name: string;
  lob?: string;
  workflow?: string;
  insured_id?: string;
  effective_date?: string;
  with_review?: boolean;
}): Promise<{ runId: string; status: string }> {
  const authToken = localStorage.getItem('authToken') || '';
  const ncToken = localStorage.getItem('amsToken') || '';
  const res = await fetch(`${CL_BASE}/runs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ ...payload, nc_token: ncToken }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).errorMessage || `Start failed (${res.status})`);
  }
  return res.json();
}

// ── Submit resume inputs ──

export async function submitResume(
  runId: string,
  stepId: string,
  inputs: Record<string, any>,
): Promise<void> {
  const authToken = localStorage.getItem('authToken') || '';
  const res = await fetch(`${CL_BASE}/runs/${runId}/resume`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ stepId, inputs }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).errorMessage || `Resume failed (${res.status})`);
  }
}

// ── Chat history persistence ──

export interface PersistedChatMsg {
  id: string;
  role: 'ai' | 'user';
  text: string;
  ts: number;
}

export async function loadRunChatMessages(runId: string): Promise<PersistedChatMsg[]> {
  const authToken = localStorage.getItem('authToken') || '';
  try {
    const res = await fetch(`${CL_BASE}/runs/${encodeURIComponent(runId)}/chat/messages`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.messages) ? data.messages : [];
  } catch {
    return [];
  }
}

export async function saveRunChatMessages(
  runId: string,
  messages: PersistedChatMsg[],
): Promise<void> {
  const authToken = localStorage.getItem('authToken') || '';
  try {
    await fetch(`${CL_BASE}/runs/${encodeURIComponent(runId)}/chat/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ messages }),
    });
  } catch {
    // fire-and-forget — errors are silent
  }
}

// ── Fetch row-level run history ──

export async function fetchRowRuns(rowId: string): Promise<{ runs: any[]; total: number }> {
  const authToken = localStorage.getItem('authToken') || '';
  try {
    const res = await fetch(`${CL_BASE}/runs/by-row/${encodeURIComponent(rowId)}?limit=20`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return { runs: [], total: 0 };
    return res.json();
  } catch {
    return { runs: [], total: 0 };
  }
}

// ── Fetch history ──

export async function fetchCommercialHistory(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  lob?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<{ runs: any[]; total: number; stats?: any }> {
  const authToken = localStorage.getItem('authToken') || '';
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.offset != null) qs.set('offset', String(params.offset));
  if (params?.search) qs.set('search', params.search);
  if (params?.lob) qs.set('lob', params.lob);
  if (params?.status) qs.set('status', params.status);
  if (params?.dateFrom) qs.set('date_from', params.dateFrom);
  if (params?.dateTo) qs.set('date_to', params.dateTo);
  const res = await fetch(`${CL_BASE}/history?${qs}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) return { runs: [], total: 0 };
  return res.json();
}

// ── Underwriting skill CRUD ──

export interface CLRSkill {
  id: string;
  name: string;
  content: string;
  updatedAt: string | null;
}

export async function fetchSkill(lob = 'WC'): Promise<CLRSkill | null> {
  const authToken = localStorage.getItem('authToken') || '';
  try {
    const res = await fetch(`${SKILLS_BASE}?lob=${lob}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.skill ?? null;
  } catch {
    return null;
  }
}

export async function saveSkill(params: {
  lob?: string;
  name?: string;
  content: string;
}): Promise<CLRSkill> {
  const authToken = localStorage.getItem('authToken') || '';
  const res = await fetch(SKILLS_BASE, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      lob: params.lob ?? 'WC',
      name: params.name ?? (params.lob === 'Cyber' ? 'Cyber Underwriting Profile' : params.lob === 'GL' ? 'GL Underwriting Profile' : params.lob === 'BOP' ? 'BOP Underwriting Profile' : 'WC Underwriting Profile'),
      content: params.content,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).errorMessage || `Save failed (${res.status})`);
  }
  const data = await res.json();
  return data.skill;
}

// ── SSE run-state stream hook ──────────────────────────────────────────────
//
// Polls the DB-backed /stream endpoint (750ms interval, keepalive every ~15s).
// Reconnects with exponential backoff on network drop or idle timeout.
// Steps accumulate across reconnects so the UI never flickers.

const STEP_IDLE_TIMEOUT_MS = 45_000; // 45s without any frame → reconnect
const STEP_IDLE_CHECK_MS   = 5_000;

export function useRunStream(runId: string | null): CLRunState | null {
  const [state, setState]       = useState<CLRunState | null>(null);
  const [reconnectKey, setReconnectKey] = useState(0);

  // Accumulated step state — persists across reconnects for same runId
  const stepsRef       = useRef<Record<string, CLRunStep>>({});
  const loadedKeyRef   = useRef<string | null>(null);
  const loadedRunIdRef = useRef<string | null>(null);
  const lastFrameAtRef    = useRef<number>(0);
  const reconnectDelayRef = useRef<number>(2_000);

  // ── Idle watchdog ─────────────────────────────────────────────────────────
  useEffect(() => {
    const isActive = state && !['completed', 'failed', 'cancelled'].includes(state.status);
    if (!isActive) return;
    const id = window.setInterval(() => {
      if (lastFrameAtRef.current > 0 && Date.now() - lastFrameAtRef.current > STEP_IDLE_TIMEOUT_MS) {
        loadedKeyRef.current = null;
        setReconnectKey(k => k + 1);
      }
    }, STEP_IDLE_CHECK_MS);
    return () => window.clearInterval(id);
  }, [state?.status]);

  // ── SSE connection ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!runId || runId === '__starting__') {
      setState(null);
      return;
    }

    const key = `${runId}:${reconnectKey}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;

    // Reset accumulated steps only when runId changes, not on reconnect
    if (loadedRunIdRef.current !== runId) {
      stepsRef.current = {};
      loadedRunIdRef.current = runId;
      setState(null);
    }

    lastFrameAtRef.current = Date.now();

    const authToken  = localStorage.getItem('authToken') || '';
    const url        = `${CL_BASE}/runs/${runId}/stream`;
    let   cancelled  = false;
    let   reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let   isTerminal = false;

    function scheduleReconnect() {
      if (isTerminal || cancelled) return;
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * 2, 30_000);
      setTimeout(() => {
        if (!cancelled) {
          loadedKeyRef.current = null;
          setReconnectKey(k => k + 1);
        }
      }, delay);
    }

    (async () => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${authToken}` } });
        if (cancelled || !res.ok || !res.body) {
          scheduleReconnect();
          return;
        }

        reader = res.body.getReader();
        reconnectDelayRef.current = 2_000; // reset on successful connect

        const decoder = new TextDecoder();
        let buffer = '';

        try {
         while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const evts = buffer.split('\n\n');
          buffer = evts.pop() ?? '';

          for (const evt of evts) {
            if (cancelled) return;
            const dataLine = evt.split('\n').find(l => l.startsWith('data:'));
            if (!dataLine) continue; // keepalive comment — ignored

            try {
              const frame = JSON.parse(dataLine.replace(/^data:\s*/, ''));
              lastFrameAtRef.current = Date.now();

              if (frame.scope === 'step') {
                stepsRef.current[frame.stepId] = {
                  stepId: frame.stepId,
                  stepIndex: frame.stepIndex,
                  stepLabel: frame.stepLabel,
                  status: frame.status,
                  durationMs: frame.durationMs ?? null,
                  error: frame.error ?? null,
                  progress: frame.progress ?? [],
                  awaitingInput: frame.awaitingInput ?? null,
                };
                const stepList = Object.values(stepsRef.current).sort((a, b) => a.stepIndex - b.stepIndex);
                const hasAwaiting = stepList.some(s => s.status === 'awaiting_input');
                const hasFailed   = stepList.some(s => s.status === 'failed');
                setState({
                  runId,
                  status: hasAwaiting ? 'awaiting_input' : hasFailed ? 'failed' : 'running',
                  durationMs: null,
                  error: null,
                  result: null,
                  steps: stepList,
                });
              }

              if (frame.scope === 'run') {
                const stepList = Object.values(stepsRef.current).sort((a, b) => a.stepIndex - b.stepIndex);
                setState(prev => ({
                  ...(prev ?? { runId, steps: stepList }),
                  runId,
                  status: frame.status,
                  durationMs: frame.durationMs ?? null,
                  error: frame.error ?? null,
                  result: frame.result ?? null,
                  steps: stepList,
                }));
                if (['completed', 'failed', 'cancelled'].includes(frame.status)) {
                  isTerminal = true;
                  reconnectDelayRef.current = 2_000;
                }
                return; // server closes stream on terminal frame
              }
            } catch {
              // ignore malformed frames
            }
          }
         }
        } catch (err: any) {
          // cancelled flag set by cleanup — not an error
          if (cancelled || err?.name === 'AbortError') return;
          throw err;
        } finally {
          // reader.cancel() returns a Promise — use .catch() not try/catch.
          // try/catch only catches synchronous throws; the async rejection from
          // cancelling an already-aborted stream needs .catch() to be suppressed.
          reader?.cancel().catch(() => {});
        }
      } catch {
        // network / fetch error — will be caught by scheduleReconnect below
      }

      scheduleReconnect();
    })();

    return () => {
      cancelled = true;
      loadedKeyRef.current = null;
      reader?.cancel().catch(() => {}); // suppress async rejection from already-aborted stream
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, reconnectKey]);

  return state;
}

